import { AuthUtils } from '@core/auth/auth.utils';

/**
 * Catálogo único de las LIAs de la plataforma: lo usan el home (tarjetas y atajos), el menú
 * superior (candado en las que el usuario no tiene) y la vitrina /home/conoce/:slug.
 */

export interface AtajoLia {
    nombre: string;
    link: string;
    icono: string;
    /** Códigos de permisos_rol que habilitan el atajo; sin esto basta con tener la LIA */
    permisos?: string[];
}

export interface VitrinaLia {
    titulo: string;
    enfasis: string;
    tagline: string;
    /** Textos de hurclick.com/soluciones (fuente: brochures comerciales) */
    beneficios: { titulo: string; texto: string }[];
    /** Slug de la página de la LIA en hurclick.com/soluciones */
    paginaWeb: string;
}

export interface Lia {
    slug: string;
    nombre: string;
    detalle: string;
    /** Entrada del módulo; es la misma ruta del ítem del menú superior */
    link: string;
    icono: string;
    /** Heroicon del menú lateral en pantallas pequeñas (fuse-vertical-navigation solo admite svgIcon) */
    iconoMenu: string;
    color: string;
    tinte: string;
    /** Cualquiera de estos códigos de permisos_rol da acceso a la LIA */
    acceso: string[];
    atajos: AtajoLia[];
    vitrina: VitrinaLia;
}

export const URL_WEB_HURCLICK = 'https://www.hurclick.com';

/** Orden de prioridad pedido por Pedro: Gestión Procesos, Afiliación, SST, Nómina, Formación */
export const LIAS: Lia[] = [
    {
        slug: 'gestion-procesos',
        nombre: 'LIA Gestión Procesos',
        detalle: 'Contratación y documentos de tus colaboradores',
        link: '/contrataciones',
        icono: 'account_tree',
        iconoMenu: 'heroicons_outline:briefcase',
        color: '#2563EB',
        tinte: '#EFF6FF',
        acceso: ['crear_proceso_contratacion', 'control_procesos_contratacion'],
        atajos: [
            // El backend exige crear_proceso_contratacion para ejecutar y control_procesos_contratacion para listar
            { nombre: 'Ejecutar proceso', link: '/contrataciones/ejecutar-procesos', icono: 'heroicons_outline:play', permisos: ['crear_proceso_contratacion'] },
            { nombre: 'Control de procesos', link: '/contrataciones/control-procesos', icono: 'heroicons_outline:view-list', permisos: ['control_procesos_contratacion'] },
        ],
        vitrina: {
            titulo: 'El ciclo completo del colaborador,',
            enfasis: 'ejecutado solo.',
            tagline: 'Mientras otros solo almacenan, BICHO ejecuta.',
            beneficios: [
                { titulo: 'Contratación 100% digital', texto: 'Firma electrónica con validez legal en Colombia. Sin impresión, sin escaneo, sin viajes para firmar.' },
                { titulo: 'Expediente único y vivo', texto: 'Cada movimiento queda registrado con trazabilidad legal. Sin reconstruir nada cuando llegue auditoría.' },
                { titulo: 'Conexión a Nómina', texto: 'La información de vinculación y novedades viaja automática a Nómina. Cero re-digitación entre sistemas.' },
                { titulo: 'Listo para auditoría en cualquier momento', texto: 'La evidencia no se construye en el momento: ya existe, porque cada paso del ciclo se registra automáticamente.' },
            ],
            paginaWeb: 'onboarding-relacion-laboral',
        },
    },
    {
        slug: 'afiliacion',
        nombre: 'LIA Afiliación',
        detalle: 'Afiliaciones y novedades de seguridad social',
        link: '/home/afiliacion',
        icono: 'how_to_reg',
        iconoMenu: 'heroicons_outline:identification',
        // cyan-700: el cyan-600 no da contraste AA con texto blanco en la portada de la vitrina
        color: '#0E7490',
        tinte: '#ECFEFF',
        // Todo lo que muestra alguna tarjeta en /home/afiliacion
        acceso: [
            'acceso_crear_afiliacion', 'acceso_control_afiliaciones', 'acceso_gestion_afiliacion',
            'acceso_anulaciones', 'acceso_radicacion_externa', 'acceso_informes_afiliacion',
            'acceso_inscribir_entidad', 'acceso_entidades_inscritas', 'acceso_inscribir_empresa_cliente',
            'acceso_empresas_clientes_inscritas', 'acceso_historial_inscripcion_empresas_cliente',
        ],
        atajos: [
            { nombre: 'Crear afiliación', link: '/home/main-afiliacion', icono: 'heroicons_outline:user-add', permisos: ['acceso_crear_afiliacion'] },
            { nombre: 'Control de afiliaciones', link: '/control-afiliacion/control_afiliacion', icono: 'heroicons_outline:clipboard-check', permisos: ['acceso_control_afiliaciones'] },
            { nombre: 'Novedades', link: '/afiliacion/gestion-novedades', icono: 'heroicons_outline:switch-horizontal', permisos: ['acceso_anulaciones'] },
            { nombre: 'Informes', link: '/informes/afiliacion', icono: 'heroicons_outline:chart-bar', permisos: ['acceso_informes_afiliacion'] },
            { nombre: 'Gestión de afiliación', link: '/afiliacion/gestion-afiliacion', icono: 'heroicons_outline:adjustments', permisos: ['acceso_gestion_afiliacion'] },
            { nombre: 'Radicación', link: '/afiliacion/radicacion-externa', icono: 'heroicons_outline:inbox-in', permisos: ['acceso_radicacion_externa'] },
        ],
        vitrina: {
            titulo: 'Cobertura activa antes de que',
            enfasis: 'tu colaborador empiece a operar.',
            tagline: 'Mientras otros solo almacenan, BICHO ejecuta.',
            beneficios: [
                { titulo: 'Afiliaciones automáticas', texto: 'EPS, AFP, ARL y CCF gestionadas. Cobertura activa antes de que el colaborador empiece a operar.' },
                { titulo: 'Expediente único y vivo', texto: 'Cada movimiento queda registrado con trazabilidad legal. Sin reconstruir nada cuando llegue auditoría.' },
                { titulo: 'Conexión a Nómina', texto: 'La información de vinculación y novedades viaja automática a Nómina. Cero re-digitación entre sistemas.' },
                { titulo: 'De un mes a menos de un día', texto: 'Caso activo: 500 afiliaciones que tomaban un mes pasaron a resolverse en menos de un día.' },
            ],
            paginaWeb: 'onboarding-relacion-laboral',
        },
    },
    {
        slug: 'sst',
        nombre: 'LIA SST',
        detalle: 'Seguridad y salud en el trabajo',
        link: '/sst',
        icono: 'health_and_safety',
        iconoMenu: 'heroicons_outline:shield-check',
        color: '#6523FF',
        tinte: '#F3EEFF',
        acceso: ['acceso_sst', 'acceso_administrador_sst'],
        atajos: [
            { nombre: 'Matriz de riesgos', link: '/sst/riesgos', icono: 'heroicons_outline:exclamation' },
            { nombre: 'Plan de trabajo', link: '/sst/plan-trabajo', icono: 'heroicons_outline:clipboard-list' },
            { nombre: 'Comités', link: '/sst/comites', icono: 'heroicons_outline:user-group' },
        ],
        vitrina: {
            titulo: 'Tu SG-SST vivo los 365 días del año.',
            enfasis: 'No solo cuando llega la ARL.',
            tagline: 'BICHO te blinda eliminando multas.',
            beneficios: [
                { titulo: 'SG-SST activo todo el año', texto: 'El sistema corre todos los días: exámenes, inspecciones, indicadores y actas del COPASST siempre al día.' },
                { titulo: 'Construido sobre la norma colombiana', texto: 'Decreto 1072 de 2015, Resolución 0312 de 2019 y metodología GTC-45. No es una adaptación de un software internacional.' },
                { titulo: 'Identificación de peligros', texto: 'Matriz IPVR activa con valoración GTC-45 y controles.' },
                { titulo: 'Autoevaluación automática', texto: 'Estándares mínimos según tamaño y nivel de riesgo, con acta y plan de mejoramiento versionado.' },
            ],
            paginaWeb: 'sg-sst',
        },
    },
    {
        slug: 'nomina',
        nombre: 'LIA Nómina',
        detalle: 'Liquida, confirma periodos y genera PILA',
        link: '/nomina',
        icono: 'payments',
        iconoMenu: 'heroicons_outline:cash',
        color: '#4F46E5',
        tinte: '#EEF2FF',
        acceso: ['acceso_nomina', 'acceso_administrador_nomina'],
        atajos: [
            { nombre: 'Periodos', link: '/nomina/periodos', icono: 'heroicons_outline:calendar' },
            { nombre: 'Liquidaciones', link: '/nomina/liquidacion', icono: 'heroicons_outline:calculator' },
            { nombre: 'PILA', link: '/nomina/documentos/pila', icono: 'heroicons_outline:document-text' },
        ],
        vitrina: {
            titulo: 'Tu nómina blindada de extremo a extremo.',
            enfasis: 'Del ingreso a la PILA, sin re-digitar nada.',
            tagline: 'Mientras otros calculan, BICHO previene.',
            beneficios: [
                { titulo: 'Información sin re-digitar', texto: 'Las novedades llegan automáticas desde Onboarding, Horarios y Relación Laboral. El analista deja de copiar.' },
                { titulo: 'Alertas en cada punto de cálculo', texto: 'Preliquidación avanzada que detecta variaciones, errores y anomalías antes de que el CFO apruebe.' },
                { titulo: 'PILA cruzada con Onboarding', texto: 'La autoliquidación se valida contra la clasificación original del empleado. Detecta riesgos UGPP antes de transmitir.' },
                { titulo: 'Soporte que responde', texto: 'Atención inmediata y parametrización flexible: el sistema se moldea a tu operación, no al revés.' },
            ],
            paginaWeb: 'nomina',
        },
    },
    {
        slug: 'formacion',
        nombre: 'LIA Formación',
        detalle: 'Cursos, evaluaciones y cobertura',
        link: '/formacion',
        icono: 'school',
        iconoMenu: 'heroicons_outline:academic-cap',
        color: '#7B3DFF',
        tinte: '#F5F0FF',
        // Formación es transversal: entra quien la tiene suelta y quien tiene SST
        acceso: ['acceso_formacion', 'acceso_sst', 'acceso_administrador_sst'],
        atajos: [
            { nombre: 'Cursos', link: '/formacion/cursos', icono: 'heroicons_outline:academic-cap' },
            { nombre: 'Plan anual', link: '/formacion/plan-anual', icono: 'heroicons_outline:calendar' },
        ],
        vitrina: {
            titulo: 'La formación que dejó de ser una lista de cursos,',
            enfasis: 'y se volvió resultados medibles.',
            tagline: 'Mientras otros registran cursos, BICHO demuestra resultados.',
            beneficios: [
                { titulo: 'Obligatoriedad bajo control', texto: 'Población objetivo, periodicidad y vencimientos, con alerta automática antes de que algo caduque.' },
                { titulo: 'Necesidad conectada a la brecha', texto: 'La capacitación nace de la evaluación de desempeño y las brechas de competencias, no de un catálogo genérico.' },
                { titulo: 'Impacto medible, no solo asistencia', texto: 'Mide aplicación, resultados y brechas de competencias efectivamente cerradas.' },
                { titulo: 'Presupuesto trazable', texto: 'Aprobado, comprometido, ejecutado y saldo, visible en el mismo sistema donde se planea y ejecuta.' },
            ],
            paginaWeb: 'formacion',
        },
    },
];

/**
 * Devuelve un verificador de permisos del usuario actual, leído del permissionsToken.
 * El superusuario tiene todo.
 */
export function permisosUsuario(): (codigos: string[]) => boolean {
    let esAdmin = false;
    let permisos: string[] = [];
    try {
        const token = localStorage.getItem('permissionsToken');
        if (token) {
            esAdmin = !!AuthUtils._getTokenUser(token);
            permisos = AuthUtils._getTokenPermissionRol(token) || [];
        }
    } catch {
        // Token ilegible: sin permisos
    }
    return (codigos: string[]) => esAdmin || codigos.some(c => permisos.includes(c));
}

/** Enlace a hurclick.com marcado para que en HubSpot se vea qué LIA pidieron y desde dónde */
export function urlWebLia(ruta: string, slug: string): string {
    return `${URL_WEB_HURCLICK}${ruta}?utm_source=plataforma&utm_medium=vitrina&utm_campaign=lia-${slug}`;
}
